import { useState, useEffect } from 'react';
import { Github, Star, GitFork } from 'lucide-react';

export default function Home() {
  const userId = "1379669300117504001";
  const githubUsername = "dorpew";
  const [profile, setProfile] = useState({
    avatar: "",
    banner: "",
    name: "",
    username: "",
    status: "offline",
    activity: null,
    spotify: null
  });
  const [elapsed, setElapsed] = useState("0:00 elapsed");
  const [repos, setRepos] = useState([]);

  const statusColors = {
    online: "bg-green-500",
    idle: "bg-yellow-500",
    dnd: "bg-red-500",
    offline: "bg-gray-500"
  };

  const formatTime = (ms) => {
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000).toString().padStart(2, "0");
    return `${m}:${s} elapsed`;
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const lanyardResponse = await fetch(`https://api.lanyard.rest/v1/users/${userId}`);
        const lanyardJson = await lanyardResponse.json();
        const data = lanyardJson.data;

        const avatarUrl = `https://cdn.discordapp.com/avatars/${userId}/${data.discord_user.avatar}.png?size=256`;
        
        let bannerUrl = "https://images.unsplash.com/photo-1519681393784-d120267933ba?q=80&w=1200";
        
        try {
          const bannerResponse = await fetch(`https://discordlookup.mesalytic.moe/v1/user/${userId}`);
          if (bannerResponse.ok) {
            const bannerData = await bannerResponse.json();
            if (bannerData.banner && bannerData.banner.link) {
              bannerUrl = bannerData.banner.link;
            }
          }
        } catch (err) {
          console.log("Failed to fetch banner, using default");
        }

        let activityData = null;
        let spotifyData = null;

        if (data.listening_to_spotify) {
          spotifyData = {
            title: "LISTENING TO SPOTIFY...",
            icon: data.spotify.album_art_url,
            name: data.spotify.song,
            details: `By ${data.spotify.artist}`,
            startTime: data.spotify.timestamps.start
          };
        } else {
          const gameActivity = data.activities.find(a => a.type === 0);
          if (gameActivity) {
            activityData = {
              title: "PLAYING A GAME",
              icon: gameActivity.assets?.large_image
                ? `https://cdn.discordapp.com/app-assets/${gameActivity.application_id}/${gameActivity.assets.large_image}.png`
                : "https://cdn.discordapp.com/embed/avatars/0.png",
              name: gameActivity.name,
              details: gameActivity.details || gameActivity.state || "",
              startTime: gameActivity.timestamps?.start
            };
          }
        }

        setProfile({
          avatar: avatarUrl,
          banner: bannerUrl,
          name: data.discord_user.display_name || data.discord_user.global_name || data.discord_user.username,
          username: data.discord_user.username,
          status: data.discord_status,
          activity: activityData,
          spotify: spotifyData
        });
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };

    loadProfile();
    const interval = setInterval(loadProfile, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadRepos = async () => {
      try {
        const response = await fetch(`https://api.github.com/users/${githubUsername}/repos?sort=updated&per_page=6`);
        const data = await response.json();
        setRepos(data);
      } catch (error) {
        console.error("Error loading repositories:", error);
      }
    };

    loadRepos();
  }, []);

  useEffect(() => {
    const currentActivity = profile.spotify || profile.activity;
    if (currentActivity?.startTime) {
      const updateTimer = () => {
        setElapsed(formatTime(Date.now() - currentActivity.startTime));
      };
      updateTimer();
      const timer = setInterval(updateTimer, 1000);
      return () => clearInterval(timer);
    }
  }, [profile.spotify, profile.activity]);

  const currentActivity = profile.spotify || profile.activity;

  return (
    <div className="min-h-screen flex flex-col items-center p-10" style={{
      background: 'radial-gradient(circle at top, #0b1220, #05070d)'
    }}>
      <div className="w-full max-w-[340px] rounded-lg overflow-hidden" style={{ backgroundColor: '#141922' }}>
        <div className="h-[120px] overflow-hidden" style={{ backgroundColor: '#0b1220' }}>
          <img 
            src={profile.banner} 
            alt="Banner" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1519681393784-d120267933ba?q=80&w=1200";
            }}
          />
        </div>

        <div className="px-4 pb-4 -mt-10 relative">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative w-20 h-20 flex-shrink-0">
              <img 
                src={profile.avatar} 
                alt="Avatar" 
                className="w-full h-full rounded-full"
                style={{ 
                  border: '6px solid #141922',
                  backgroundColor: '#0b1220'
                }}
              />
              <div 
                className={`absolute bottom-0 right-0 w-6 h-6 rounded-full ${statusColors[profile.status]}`}
                style={{ border: '6px solid #141922' }}
              />
            </div>

            <div className="flex-1">
              <div className="text-xl font-semibold text-white">
                {profile.name}
              </div>
              <div className="text-sm" style={{ color: '#9aa4b2' }}>
                {profile.username}
              </div>
            </div>
          </div>
        </div>

        <div className="h-px mx-4" style={{ backgroundColor: '#0b1220' }} />

        {currentActivity ? (
          <div className="p-4">
            <div className="text-xs font-bold mb-2 tracking-wide" style={{ color: '#8b9cff' }}>
              {currentActivity.title}
            </div>
            <div className="flex gap-3 items-start">
              <div 
                className="w-[60px] h-[60px] rounded-lg overflow-hidden flex-shrink-0"
                style={{ backgroundColor: '#0b1220' }}
              >
                <img 
                  src={currentActivity.icon} 
                  alt="Activity" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">
                  {currentActivity.name}
                </div>
                <div className="text-sm truncate" style={{ color: '#cbd5f5' }}>
                  {currentActivity.details}
                </div>
                <div className="text-xs mt-1 opacity-70" style={{ color: '#9aa4b2' }}>
                  {elapsed}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 text-center text-sm" style={{ color: '#9aa4b2' }}>
            No activity at the moment.
          </div>
        )}

        <div className="px-4 py-3 flex justify-center gap-4">
          <a 
            href="https://github.com/dorpew" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all hover:opacity-70"
            style={{ backgroundColor: '#141922' }}
          >
            <Github className="w-5 h-5" style={{ color: '#9aa4b2' }} />
          </a>
          <a 
            href="https://tiktok.com/@dorpew" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all hover:opacity-70"
            style={{ backgroundColor: '#141922' }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#9aa4b2' }}>
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
          </a>
          <a 
            href="https://open.spotify.com/user/31tluf6bcxkmtjegnnjxdfupwj5a?si=mxU8yujqS7uft2jvrsSknw" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all hover:opacity-70"
            style={{ backgroundColor: '#141922' }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#9aa4b2' }}>
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
          </a>
          <a 
            href="https://discord.com/users/1379669300117504001" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all hover:opacity-70"
            style={{ backgroundColor: '#141922' }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#9aa4b2' }}>
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
          </a>
        </div>
      </div>

      <div className="w-full max-w-[340px] rounded-lg overflow-hidden mt-5" style={{ backgroundColor: '#141922' }}>
        <div className="p-4">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-white">Public Repositories</h2>
          </div>
          
          <div className="space-y-3">
            {repos.length === 0 ? (
              <div className="text-center py-4" style={{ color: '#9aa4b2' }}>
                Loading repositories...
              </div>
            ) : (
              repos.map((repo) => (
                <a
                  key={repo.id}
                  href={repo.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-3 rounded-lg transition-all hover:opacity-80"
                  style={{ backgroundColor: '#0b1220' }}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-white truncate flex-1">
                      {repo.name}
                    </h3>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {repo.stargazers_count > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3" style={{ color: '#9aa4b2' }} />
                          <span className="text-xs" style={{ color: '#9aa4b2' }}>
                            {repo.stargazers_count}
                          </span>
                        </div>
                      )}
                      {repo.forks_count > 0 && (
                        <div className="flex items-center gap-1">
                          <GitFork className="w-3 h-3" style={{ color: '#9aa4b2' }} />
                          <span className="text-xs" style={{ color: '#9aa4b2' }}>
                            {repo.forks_count}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {repo.description && (
                    <p className="text-xs line-clamp-2 mb-2" style={{ color: '#cbd5f5' }}>
                      {repo.description}
                    </p>
                  )}
                  {repo.language && (
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: getLanguageColor(repo.language) }} />
                      <span className="text-xs" style={{ color: '#9aa4b2' }}>
                        {repo.language}
                      </span>
                    </div>
                  )}
                </a>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function getLanguageColor(language) {
  const colors = {
  HTML: '#e34c26',
  CSS: '#563d7c',
  JavaScript: '#f1e05a',
  TypeScript: '#3178c6',
  Python: '#3572A5',
  Java: '#b07219',
  Go: '#00ADD8',
  Rust: '#dea584',
  Ruby: '#701516',
  PHP: '#4F5D95',
  Swift: '#F05138',
  Kotlin: '#A97BFF',
  Dart: '#0175C2',
  Scala: '#c22d40',
  Groovy: '#4298b8',
  Elixir: '#6e4a7e',
  Erlang: '#B83998',
  Haskell: '#5e5086',
  Nim: '#ffc200',
  Zig: '#ec915c',
  C: '#555555',
  'C++': '#00599C',
  'C#': '#178600',
  ObjectiveC: '#438eff',
  ObjectiveCpp: '#6866fb',
  Shell: '#89e051',
  Bash: '#89e051',
  PowerShell: '#012456',
  Batchfile: '#C1F12E',
  Assembly: '#6E4C13',
  Smali: '#596706',
  JSON: '#292929',
  YAML: '#cb171e',
  TOML: '#9c4221',
  XML: '#0060ac',
  SQL: '#e38c00',
  PLpgSQL: '#336790',
  Vue: '#41b883',
  Svelte: '#ff3e00',
  GDScript: '#355570',
  ShaderLab: '#222c37',
  V: '#4f87c4',
  Crystal: '#000100',
  Fortran: '#4d41b1',
  R: '#198CE7',
  Julia: '#a270ba',
  Matlab: '#e16737',
  Markdown: '#083fa1',
  LaTeX: '#3D6117',
  Lua: '#000080',
  Perl: '#0298c3',
  OCaml: '#3be133',
  ReasonML: '#ff5847',
  FSharp: '#b845fc',
  Hack: '#878787',
  Apex: '#1797c0',
  Solidity: '#AA6746',
  WebAssembly: '#04133b',
  Nix: '#7e7eff',
  OpenEdgeABL: '#5ce600',
  Red: '#f50000',
  Ring: '#2D54CB',
  Smalltalk: '#596706',
  Tcl: '#e4cc98',
  Awk: '#c30e9b',
  Brainfuck: '#2F2530',
  Chapel: '#8dc63f',
  COBOL: '#003a8f',
  CommonLisp: '#3fb68b',
  D: '#ba595e',
  Idris: '#b30000',
  Io: '#a9188d',
  J: '#9EEDFF',
  MonkeyC: '#8D6748',
  Oz: '#fab738',
  Pony: '#f99b15',
  Prolog: '#74283c',
  Qsharp: '#fed659',
  Redscript: '#ff0000',
  Scratch: '#4D97FF',
  SuperCollider: '#46390b',
  Unrealscript: '#a54c4d',
  XQuery: '#5232e7'
};
  return colors[language] || '#9aa4b2';
              }
